import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link as MLink,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import BillingBreakdownDisplay, { BillingBreakdownLine } from '../components/BillingBreakdownDisplay';
import { userCanAccessFirm } from '../utils/firmAccess';

interface ClientEstimate {
  id: number;
  case_id: number;
  billing_type: string;
  quantity: number;
  total_pages?: number | null;
  reviewed_pages?: number | null;
  appendix_included?: boolean;
  total_amount: number;
  status: string;
  created_at: string;
  is_invoiced?: boolean;
  billing_breakdown?: { lines: BillingBreakdownLine[] } | null;
  case?: {
    id: number;
    title: string;
    subtitle?: string;
    project?: { id: number; name: string };
  };
  createdBy?: { id: number; name: string };
}

const ClientEstimatesList: React.FC = () => {
  const { firmId } = useParams<{ firmId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [estimates, setEstimates] = useState<ClientEstimate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [firmName, setFirmName] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const isClientUser = () => {
    if (user?.is_finance_user) return true;
    if (!user?.roles) return false;
    return user.roles.some((role) =>
      ['firm_admin', 'firm_main', 'client_admin', 'client_manager', 'client_finance_user'].includes(role.slug)
    );
  };

  const isPmpAdmin = () => user?.roles?.some((role) => role.slug === 'pmp_admin') ?? false;
  const isSiteAdmin = () => user?.roles?.some((role) => role.slug === 'site_admin') ?? false;
  const canViewBreakdown = () => isPmpAdmin() || isClientUser();
  const canAccessThisFirm = isPmpAdmin() || isSiteAdmin() || userCanAccessFirm(user, firmId);

  useEffect(() => {
    loadFirmName();
  }, [firmId]);

  useEffect(() => {
    loadEstimates();
  }, [firmId, statusFilter]);

  const loadFirmName = async () => {
    try {
      const response = await api.get(`/firms/${firmId}`);
      if (response.data.success) {
        setFirmName(response.data.data.name);
      }
    } catch {
      // ignore
    }
  };

  const loadEstimates = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/client-estimates', {
        params: { firm_id: firmId, status: statusFilter },
      });
      if (response.data.success) {
        setEstimates(response.data.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load estimates');
    } finally {
      setLoading(false);
    }
  };

  const getBillingTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      page_based: 'Page Based',
      consolidation: 'Consolidation',
      time_based: 'Time Based',
      case_based: 'Case Based',
      flat_rate: 'Flat Rate',
    };
    return labels[type] || type;
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'default',
      pending: 'warning',
      approved: 'success',
      rejected: 'error',
    };
    return colors[status] || 'default';
  };

  if (!isClientUser() && !isPmpAdmin() && !isSiteAdmin()) {
    return (
      <Box>
        <Alert severity="error">You don't have permission to access this page.</Alert>
      </Box>
    );
  }

  if (!canAccessThisFirm) {
    return (
      <Box>
        <Alert severity="error">You can only view estimates for your own firm.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 2 }}>
        <MLink color="inherit" href="#" onClick={(e) => { e.preventDefault(); navigate('/firms'); }} sx={{ textDecoration: 'none' }}>
          Firms
        </MLink>
        <MLink color="inherit" href="#" onClick={(e) => { e.preventDefault(); navigate(`/firms/${firmId}`); }} sx={{ textDecoration: 'none' }}>
          {firmName || `Firm #${firmId}`}
        </MLink>
        <Typography color="text.primary">Client Estimates</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Client Estimates</Typography>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            {isPmpAdmin() && <MenuItem value="draft">Draft</MenuItem>}
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : estimates.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">No estimates found</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Case</strong></TableCell>
                <TableCell><strong>Project</strong></TableCell>
                <TableCell><strong>Billing Type</strong></TableCell>
                <TableCell><strong>Pages</strong></TableCell>
                {canViewBreakdown() && <TableCell><strong>Total</strong></TableCell>}
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Created</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {estimates.map((estimate) => (
                <React.Fragment key={estimate.id}>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">{estimate.case?.title || `Case #${estimate.case_id}`}</Typography>
                    </TableCell>
                    <TableCell>{estimate.case?.project?.name || '—'}</TableCell>
                    <TableCell>{getBillingTypeLabel(estimate.billing_type)}</TableCell>
                    <TableCell>
                      {estimate.total_pages != null ? (
                        <>
                          {estimate.total_pages} total
                          {estimate.reviewed_pages != null && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {estimate.reviewed_pages} reviewed
                            </Typography>
                          )}
                          {estimate.appendix_included && (
                            <Typography variant="caption" display="block" color="text.secondary">Appendix</Typography>
                          )}
                        </>
                      ) : (
                        estimate.quantity
                      )}
                    </TableCell>
                    {canViewBreakdown() && (
                      <TableCell>${Number(estimate.total_amount).toFixed(2)}</TableCell>
                    )}
                    <TableCell>
                      <Chip label={estimate.status.toUpperCase()} color={getStatusColor(estimate.status) as any} size="small" />
                      {estimate.is_invoiced && <Chip label="INVOICED" color="info" size="small" sx={{ ml: 0.5 }} />}
                    </TableCell>
                    <TableCell>{new Date(estimate.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => navigate(`/cases/${estimate.case_id}`)}>View Case</Button>
                      {canViewBreakdown() && estimate.billing_breakdown?.lines?.length ? (
                        <Button size="small" onClick={() => setExpandedId(expandedId === estimate.id ? null : estimate.id)}>
                          {expandedId === estimate.id ? 'Hide' : 'Breakdown'}
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                  {expandedId === estimate.id && estimate.billing_breakdown?.lines?.length ? (
                    <TableRow>
                      <TableCell colSpan={canViewBreakdown() ? 8 : 7}>
                        <BillingBreakdownDisplay
                          lines={estimate.billing_breakdown.lines}
                          totalAmount={Number(estimate.total_amount)}
                        />
                      </TableCell>
                    </TableRow>
                  ) : null}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ClientEstimatesList;
